import React, { useState, useEffect } from 'react';
import { tasksAPI, Task } from '../api/api';
import { useAuth } from '../context/AuthContext';
import Navbar from './Navbar';
import TaskModal from './TaskModal';
import './MyTasks.css';
import './Modal.css';

const TasksForReview: React.FC = () => {
  const { user, isImpersonating } = useAuth();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null);
  const [rejectingTaskId, setRejectingTaskId] = useState<number | null>(null);
  const [rejectComment, setRejectComment] = useState('');

  useEffect(() => {
    loadTasks();
  }, [user?.id, isImpersonating]);

  const loadTasks = async () => {
    try {
      setLoading(true);
      const data = await tasksAPI.getTasksForReview();
      setTasks(data);
    } catch (error) {
      console.error('Ошибка при загрузке задач на проверке:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveTask = async (taskId: number) => {
    if (!window.confirm('Одобрить и завершить задачу?')) {
      return;
    }

    try {
      await tasksAPI.approveTask(taskId);
      loadTasks();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Ошибка при одобрении задачи');
    }
  };

  const handleRejectTask = async () => {
    if (!rejectingTaskId) return;
    if (!rejectComment.trim()) {
      alert('Пожалуйста, укажите причину отклонения');
      return;
    }

    try {
      await tasksAPI.rejectTask(rejectingTaskId, rejectComment);
      setRejectingTaskId(null);
      setRejectComment('');
      loadTasks();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Ошибка при отклонении задачи');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'in_review':
        return '#9b59b6';
      default:
        return '#95a5a6';
    }
  };

  const isOverdue = (deadline: string | null | undefined) => {
    if (!deadline) return false;
    return new Date(deadline) < new Date();
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="loading">Загрузка...</div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="my-tasks-container">
        <div className="my-tasks-header">
          <h1>На проверке</h1>
        </div>

        <div className="tasks-list">
          {tasks.length === 0 ? (
            <div className="empty-state">Нет задач на проверке</div>
          ) : (
            tasks.map((task) => (
              <div key={task.id} className="my-task-card">
                <div className="task-card-header">
                  <h3 onClick={() => setSelectedTaskId(task.id)}>{task.title}</h3>
                  <span
                    className="status-badge"
                    style={{ backgroundColor: getStatusColor(task.status) }}
                  >
                    На проверке
                  </span>
                </div>
                {task.description && (
                  <p className="task-description">{task.description}</p>
                )}
                <div className="task-info">
                  {task.board_name && (
                    <span className="task-meta">📋 {task.board_name}</span>
                  )}
                  {task.column_name && (
                    <span className="task-meta">📌 {task.column_name}</span>
                  )}
                  {task.assigned_to_username && (
                    <span className="task-meta">👤 Исполнитель: {task.assigned_to_username}</span>
                  )}
                  {task.deadline ? (
                    <span
                      className={`task-deadline ${isOverdue(task.deadline) ? 'overdue' : ''}`}
                    >
                      ⏰ {new Date(task.deadline).toLocaleString('ru-RU')}
                      {isOverdue(task.deadline) && ' (Просрочено)'}
                    </span>
                  ) : (
                    <span className="task-deadline">
                      ⏰ Задача со свободным сроком
                    </span>
                  )}
                </div>
                <div className="task-actions">
                  <div className="task-actions-row">
                    <button
                      onClick={() => setSelectedTaskId(task.id)}
                      className="view-btn task-btn-half"
                    >
                      Посмотреть
                    </button>
                    <button
                      onClick={() => handleApproveTask(task.id)}
                      className="approve-btn task-btn-half"
                    >
                      Одобрить
                    </button>
                  </div>
                  <button
                    onClick={() => setRejectingTaskId(task.id)}
                    className="reject-btn task-btn-full"
                  >
                    Отклонить
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {selectedTaskId && (
          <TaskModal
            taskId={selectedTaskId}
            onClose={() => {
              setSelectedTaskId(null);
            }}
            onTaskUpdated={loadTasks}
          />
        )}

        {rejectingTaskId && (
          <div className="modal-overlay" onClick={() => { setRejectingTaskId(null); setRejectComment(''); }}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Отклонить задачу</h2>
                <button className="close-btn" onClick={() => { setRejectingTaskId(null); setRejectComment(''); }}>×</button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label>Причина отклонения (комментарий) *</label>
                  <textarea
                    value={rejectComment}
                    onChange={(e) => setRejectComment(e.target.value)}
                    rows={4}
                    placeholder="Укажите, что нужно исправить..."
                  />
                </div>
                <div className="modal-actions">
                  <button onClick={() => { setRejectingTaskId(null); setRejectComment(''); }} className="cancel-btn">
                    Отмена
                  </button>
                  <button onClick={handleRejectTask} className="reject-btn">
                    Отклонить
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default TasksForReview;

